import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography } from "@mui/material";
import EntityHubLayout, { PAGE_SIZE } from "../../components/product-manager/EntityHubLayout";
import EntityAssetCard from "../../components/product-manager/EntityAssetCard";
import BulkEntityUploadDialog from "../../components/product-manager/BulkEntityUploadDialog";
import EditGatewayRouteDialog from "../../components/product-manager/EditGatewayRouteDialog";
import { GATEWAY_ROUTE_BULK_CONFIG } from "../../utils/bulkUpload/bulkEntityConfigs";
import { useBookmarkableHubState } from "../../hooks/useBookmarkableHubState";
import { useGatewayRoutes } from "../../hooks/useGatewayRoutes";
import { apiService } from "../../api/services/apiService";
import type { GatewayRoute } from "../../types";
import { EntityStatus } from "../../types";
import { pmHubAddPath } from "../../utils/pmHubRoutes";
import { dataSensitivityLabel } from "../../utils/dataSensitivity";

function filterRoutes(
  items: GatewayRoute[],
  state: ReturnType<typeof useBookmarkableHubState>,
): GatewayRoute[] {
  let result = items;
  if (state.debouncedQ) {
    const q = state.debouncedQ.toLowerCase();
    result = result.filter((r) => {
      const fields = [
        r.name,
        r.basePath,
        r.upstreamServer,
        r.upstreamPath,
        r.envName,
        r.gatewayId,
        r.purpose ?? "",
      ];
      return fields.some((f) => (f ?? "").toLowerCase().includes(q));
    });
  }
  if (state.statuses.length) {
    result = result.filter((r) => state.statuses.includes(r.status));
  }
  return result;
}

const GatewayRouteHubPage: React.FC = () => {
  const hubState = useBookmarkableHubState({ statusOptions: Object.values(EntityStatus) });
  const { gatewayRoutes: fetched, loading, error, refresh } = useGatewayRoutes();
  const [routes, setRoutes] = useState<GatewayRoute[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);

  useEffect(() => {
    setRoutes(fetched);
  }, [fetched]);

  const filtered = useMemo(() => filterRoutes(routes, hubState), [routes, hubState]);

  const paginated = useMemo(() => {
    const start = (hubState.page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, hubState.page]);

  const editItem = useMemo(
    () => routes.find((r) => r.uuid === hubState.editUuid) ?? null,
    [routes, hubState.editUuid],
  );

  const duplicateKeys = useMemo(() => {
    const keys = new Set<string>();
    const seen = new Map<string, string>();
    for (const r of routes) {
      const key = `${r.envName}|${r.gatewayId}|${r.basePath}`;
      if (seen.has(key)) {
        keys.add(key);
        keys.add(seen.get(key)!);
      } else {
        seen.set(key, key);
      }
    }
    return keys;
  }, [routes]);

  const handleToggle = async (item: GatewayRoute) => {
    setToggleLoadingId(item.uuid);
    const newStatus =
      item.status === EntityStatus.ACTIVE ? EntityStatus.INACTIVE : EntityStatus.ACTIVE;
    try {
      const updated = await apiService.updateGatewayRoute(item.uuid, { status: newStatus });
      setRoutes((prev) => prev.map((r) => (r.uuid === updated.uuid ? updated : r)));
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setToggleLoadingId(null);
    }
  };

  return (
    <>
      <EntityHubLayout
        hubTitle="Gateway Route Hub"
        hubDescription="Confidential routing documentation: gateway paths, upstream targets, auth, and optional SLI/SLO targets. Not visible to partners."
        addPath={pmHubAddPath("gatewayRoute")}
        addLabel="Add route"
        onBulkUpload={() => setBulkOpen(true)}
        loading={loading}
        error={error}
        itemCount={filtered.length}
        hubState={hubState}
      >
        <Grid container spacing={2}>
          {paginated.map((item) => {
            const dupKey = `${item.envName}|${item.gatewayId}|${item.basePath}`;
            return (
              <Grid item xs={12} sm={6} md={4} key={item.uuid}>
                <EntityAssetCard
                  title={item.name}
                  subtitle={`${item.envName} · ${item.basePath} → ${item.upstreamServer}${item.upstreamPath}`}
                  status={item.status}
                  enabled={item.status === EntityStatus.ACTIVE}
                  onToggleEnabled={() => handleToggle(item)}
                  toggleLoading={toggleLoadingId === item.uuid}
                  onEdit={() => hubState.setEditUuid(item.uuid)}
                  onDelete={async () => {
                    if (!window.confirm(`Delete route ${item.name}?`)) return;
                    await apiService.deleteGatewayRoute(item.uuid);
                    refresh();
                  }}
                  metadata={
                    <>
                      {duplicateKeys.has(dupKey) && (
                        <Typography variant="caption" color="warning.main" display="block">
                          Possible duplicate
                        </Typography>
                      )}
                      {item.dataSensitivity && (
                        <Typography variant="caption" color="text.secondary">
                          {dataSensitivityLabel(item.dataSensitivity)}
                        </Typography>
                      )}
                    </>
                  }
                />
              </Grid>
            );
          })}
        </Grid>
      </EntityHubLayout>

      <BulkEntityUploadDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        config={GATEWAY_ROUTE_BULK_CONFIG}
        onComplete={() => {
          setBulkOpen(false);
          refresh();
        }}
      />

      {editItem && (
        <EditGatewayRouteDialog
          route={editItem}
          open={!!hubState.editUuid}
          onClose={() => hubState.setEditUuid(null)}
          onSaved={(updated) => {
            setRoutes((prev) => prev.map((r) => (r.uuid === updated.uuid ? updated : r)));
            hubState.setEditUuid(null);
          }}
        />
      )}
    </>
  );
};

export default GatewayRouteHubPage;
